import { FormCommonProps } from "src/components/common/forms/types.ts";
import { AavoForm } from "src/components/common/forms/AavoForm.tsx";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import i18n from "i18next";
import { ConfigurableObjectType } from "src/api/generated/erp/db/types/enums/configurableObjectType.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { ObjectConfigurationHistoryEntryInput } from "src/api/generated/erp/configurator/objectConfigurationHistory/objectConfigurationHistoryEntryInput.ts";
import { useErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialogContext.tsx";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { ConfiguratorInput } from "src/api/generated/io/aavo/applications/db/erp/types/configuratorInput.ts";
import { InputAdornment } from "@mui/material";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton.tsx";
import { faCircleNodes } from "@fortawesome/pro-regular-svg-icons";
import { useState } from "react";
import { ConfigurationCheckpointApi } from "src/api/generated/erp/configurator/checkpoints/api/configurationCheckpointApi.ts";
import { Control, UseFormSetValue } from "react-hook-form";
import { ConfigurationCheckpointState } from "src/components/views/erp/configurator/configuratorForm/checkpoints/configurationCheckpoints.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface ObjectConfigurationHistoryEntryInputFormProps extends FormCommonProps<ObjectConfigurationHistoryEntryInput> {
	objectType: ConfigurableObjectType;
	objectId: number;
	configurationSessionId: string;
	configuratorType: PartConfiguratorType;
	configuratorInput: ConfiguratorInput;
	checkpointState: ConfigurationCheckpointState;
}

export const ObjectConfigurationHistoryEntryInputForm = (props: ObjectConfigurationHistoryEntryInputFormProps) => {
	const { onCompleted, onFormEdited } = props;
	return (
		<AavoForm<ObjectConfigurationHistoryEntryInput, ObjectConfigurationHistoryEntryInput>
			defaultValues={{
				comment: "",
				keepCurrentEntry: false,
			}}
			submit={(values) => values}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.configurator.objectConfigurationHistory.entryInput.form"),
				data: {
					objectType: props.objectType,
					objectId: props.objectId,
					configurationSessionId: props.configurationSessionId,
				},
			}}
			render={({ control, setValue }) => (
				<ObjectConfigurationHistoryEntryInputFormContent control={control} setValue={setValue} {...props} />
			)}
		/>
	);
};

interface ObjectConfigurationHistoryEntryInputFormContentProps extends ObjectConfigurationHistoryEntryInputFormProps {
	control: Control<ObjectConfigurationHistoryEntryInput>;
	setValue: UseFormSetValue<ObjectConfigurationHistoryEntryInput>;
}

const ObjectConfigurationHistoryEntryInputFormContent = ({
	control,
	setValue,
	configurationSessionId,
	configuratorType,
	configuratorInput,
	checkpointState,
}: ObjectConfigurationHistoryEntryInputFormContentProps) => {
	const { logErrorAndShowOnDialog } = useErrorDialog();
	const [loading, setLoading] = useState(false);

	return (
		<>
			<FormCheckbox control={control} name={"keepCurrentEntry"} label={i18n.t("keep_previous_configuration")} />
			<FormTextField
				control={control}
				name={"comment"}
				label={i18n.t("comment_for_change_log")}
				autoFocus
				multiline
				slotProps={{
					input: {
						// The summary is generated from the component form's input, which property-model forms do not have.
						endAdornment: configuratorInput.inputComponentValues.length > 0 && (
							<InputAdornment position="end" sx={{ alignSelf: "flex-end" }}>
								<AavoIconButton
									icon={faCircleNodes}
									tooltip={i18n.t("generate_ai_summary")}
									loading={loading}
									onClick={onGenerateAiSummary}
								/>
							</InputAdornment>
						),
					},
				}}
			/>
		</>
	);

	async function onGenerateAiSummary() {
		setLoading(true);
		try {
			const description = await ConfigurationCheckpointApi.generateHistoryDescription({
				configurationSessionId: configurationSessionId,
				configuratorType: configuratorType,
				configuratorInput: configuratorInput,
				latestCheckpointPropertyValues: checkpointState.previousPropertyValues,
				checkpoints: checkpointState.entries.map((entry) => ({
					description: entry.description,
					price: entry.price,
				})),
			});
			setValue("comment", description);
		} catch (e) {
			logErrorAndShowOnDialog(e);
		} finally {
			setLoading(false);
		}
	}
};
